import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { addActivityInGroup, type INewActivityInGroup } from "@/api/Activities";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import CheckmarkIcon from "@medoma/components/icons/CheckmarkIcon";
import { deducedError } from "@/Utils/ErrorUtils";
import { generateRandomUUID } from "@/Utils/UniqueId";
import { TitleInput } from "@/forms/AddActivity/TitleInput";
import { DescriptionInput } from "@/forms/AddActivity/DescriptionInput";
import type { IEmployeeCompetence } from "@medoma/models/shifts";
import { defaultRequiredCompetences } from "@medoma/models/activityRequirements";
import type { ITitleInputFields } from "@/forms/AddActivity/TitleInput";
import type { IDescriptionInputFields } from "@/forms/AddActivity/DescriptionInput";
import type { ICompetencePickerFields } from "@/forms/AddActivity/CompetencePicker";
import { CompetencePicker } from "@/forms/AddActivity/CompetencePicker";
import {
  ActivityDuration,
  type IActivityDurationFields,
} from "@/forms/AddActivity/ActivityDuration";
import { DEFAULT_ACTIVITY_DURATION_OPTION } from "@/forms/AddActivity/activityDurationModels";
import { convertDurationToMinutes } from "@/forms/AddActivity/activityDurationUtils";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";

type IAddActivityInGroupFormData = ITitleInputFields &
  IDescriptionInputFields &
  ICompetencePickerFields &
  IActivityDurationFields;

export const AddActivityInGroupForm = ({
  onSuccess,
  groupId,
  currentEmployeeCompetence,
}: {
  onSuccess: () => void;
  groupId: string;
  currentEmployeeCompetence: IEmployeeCompetence;
}) => {
  const { t } = useLingui();
  const { data: showDuration } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const methods = useForm<IAddActivityInGroupFormData>({
    defaultValues: {
      requiredCompetences: defaultRequiredCompetences(
        currentEmployeeCompetence,
      ),
      durationOption: DEFAULT_ACTIVITY_DURATION_OPTION,
    },
  });
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    reset,
    setError,
  } = methods;

  const { mutate: addActivityMutation, isPending: isAdding } = useMutation({
    mutationFn: ({ newActivity }: { newActivity: INewActivityInGroup }) =>
      addActivityInGroup(newActivity),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: () => {
      reset();
      return onSuccess();
    },
  });

  return (
    <FormProvider {...methods}>
      <Form
        onSubmit={handleSubmit((formData) => {
          const {
            title,
            description,
            requiredCompetences,
            durationOption,
            customDuration,
            customDurationUnit,
          } = formData;

          // Convert duration fields to minutes
          const duration = convertDurationToMinutes(
            durationOption,
            customDuration,
            customDurationUnit,
          );

          const newActivity: INewActivityInGroup = {
            activityId: generateRandomUUID(),
            title,
            description,
            groupId,
            requiredCompetences,
            duration,
          };

          addActivityMutation({ newActivity });
        })}
      >
        {isAdding ? (
          <Loading message={t`Lägger till aktivitet`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : null}
            <TitleInput />
            <DescriptionInput showHelperText={true} />
            {showDuration ? <ActivityDuration /> : null}
            <CompetencePicker isRequired={true} />
            <FilledButton type="submit" width="fill-container">
              <CheckmarkIcon />
              <Trans>Lägg till aktivitet</Trans>
            </FilledButton>
          </>
        )}
      </Form>
    </FormProvider>
  );
};
